/**
 * English: Persist conversation_id per org+station scope so GET /api/chat/history matches Redis conv:{id}
 *         and switching tenant does not mix transcripts.
 * Thai: เก็บ conversation_id ต่อขอบเขต org+สถานี ให้ตรงกับ Redis และไม่ปนประวัติเมื่อเปลี่ยน tenant
 */
const MAP_KEY = 'agentstation_chat_sessions_v3';
/** Legacy single key — removed by clearAllChatSessionStorage */
const LEGACY_SINGLE_KEY = 'agentstation_chat_session_id';

function readMap(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(MAP_KEY);
    if (!raw) return {};
    const o = JSON.parse(raw) as unknown;
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return {};
    return o as Record<string, string>;
  } catch {
    return {};
  }
}

function writeMap(m: Record<string, string>): void {
  try {
    sessionStorage.setItem(MAP_KEY, JSON.stringify(m));
  } catch {
    /* private mode / quota */
  }
}

/**
 * English: Stable scope for sessionStorage map — org + station + user subject (JWT sub / user_id / …).
 * Thai: ขอบเขตสำหรับ map — org + สถานี + ตัวตนผู้ใช้ (แยก Redis transcript ต่อ user; long-term ยัง org+station ฝั่ง backend)
 */
export function buildChatSessionScopeKey(
  userOrgName: string,
  activeStationId: string,
  userSubject: string,
): string {
  const org = (userOrgName || '').trim() || '_';
  const st = (activeStationId || '').trim() || '_';
  const u = (userSubject || '').trim() || '_';
  return `${org}::${st}::${u}`;
}

export function getOrCreateChatSessionId(scopeKey: string): string {
  const map = readMap();
  const existing = map[scopeKey];
  if (existing && existing.length >= 8) return existing;
  const id = crypto.randomUUID();
  map[scopeKey] = id;
  writeMap(map);
  return id;
}

/** Overwrite session id for a scope — used when backend returns a different conversation_id. */
export function setChatSessionId(scopeKey: string, id: string): void {
  const map = readMap();
  map[scopeKey] = id;
  writeMap(map);
}

/** New session for this scope — call on "Clear" so Redis uses a fresh conv: key from the next send. */
export function resetChatSessionId(scopeKey: string): string {
  const map = readMap();
  const id = crypto.randomUUID();
  map[scopeKey] = id;
  writeMap(map);
  return id;
}

/** Call on logout (and optionally on forced session end) so the next login does not reuse UUIDs. */
export function clearAllChatSessionStorage(): void {
  try {
    sessionStorage.removeItem(MAP_KEY);
    sessionStorage.removeItem(LEGACY_SINGLE_KEY);
  } catch {
    /* ignore */
  }
}
